import React, { useState, useEffect } from 'react';
import { getMenuItems } from '../../services/api';
import { createMenuItem, updateMenuItem, deleteMenuItem } from '../../services/adminApi';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from '../../hooks/use-toast';
import { Pencil, Trash2, Plus, ChevronDown, ChevronUp, GripVertical, X, Eye, EyeOff, DollarSign, Settings, Upload, Image } from 'lucide-react';
import ImagePickerModal from './components/ImagePickerModal';

// Helper to get full image URL for uploads (use relative URLs)
const getUploadImageUrl = (imageUrl) => {
  if (!imageUrl) return '';
  
  // NEW: Static menu uploads (persisted in git) - use as-is
  if (imageUrl.startsWith('/images/menu-uploads/')) {
    return imageUrl;
  }
  
  // OLD: Backend-served uploads - add /api prefix if needed
  if (imageUrl.startsWith('/images/uploads/')) {
    return `/api${imageUrl}`;
  }
  if (imageUrl.startsWith('/api/images/uploads/')) {
    return imageUrl;
  }
  
  return imageUrl;
};

const MenuItems = () => {
  const [items, setItems] = useState([]);
  const [brokenImageIds, setBrokenImageIds] = useState(new Set());
  const [brokenImages, setBrokenImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [useSizes, setUseSizes] = useState(false); // Toggle between single price and sizes
  const [editingCategoryPricing, setEditingCategoryPricing] = useState(null); // For category pricing dialog
  const [isDragging, setIsDragging] = useState(false); // For drag & drop image upload
  const [showImagePicker, setShowImagePicker] = useState(false); // For media gallery picker
  const [imagePickerTarget, setImagePickerTarget] = useState(null); // 'main' or size index
  const [categoryPricingForm, setCategoryPricingForm] = useState({
    pricing_type: 'individual',
    shared_prices: []
  });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    sizes: [], // Array of {name, price, description, image_url, image_alt}
    show_price: true,
    show_image: true,
    image_url: '',
    image_alt: '', // Alt text for SEO
    category: 'ice_cream',
    display_page: 'menu', // "menu", "specials", or "both"
    featured: false,
    famous: false,
    active: true,
    order: 0
  });

  // Categories loaded from backend with visibility settings
  const [categories, setCategories] = useState([]);

  // Default categories (fallback)
  const defaultCategories = [
    { id: 'specialty', value: 'specialty', label: 'Specialty Items', icon: '⭐', color: 'bg-yellow-100 border-yellow-300', visible: true, order: 1 },
    { id: 'ice_cream_nachos', value: 'ice_cream_nachos', label: 'Ice Cream Nachos', icon: '🧇', color: 'bg-rose-100 border-rose-300', visible: true, order: 2 },
    { id: 'stackers', value: 'stackers', label: 'Ice Cream Stackers', icon: '🍨', color: 'bg-pink-100 border-pink-300', visible: true, order: 3 },
    { id: 'scoops', value: 'scoops', label: 'Scoops', icon: '🍦', color: 'bg-purple-100 border-purple-300', visible: true, order: 4 },
    { id: 'treats', value: 'treats', label: 'Ice Cream Treats', icon: '🍌', color: 'bg-orange-100 border-orange-300', visible: true, order: 5 },
    { id: 'pastries', value: 'pastries', label: 'Pastries', icon: '🥐', color: 'bg-amber-100 border-amber-300', visible: true, order: 6 },
    { id: 'drinks', value: 'drinks', label: 'Drinks & Coffee', icon: '☕', color: 'bg-blue-100 border-blue-300', visible: true, order: 7 },
    { id: 'energy_flavors', value: 'energy_flavors', label: 'Energy Drink Flavors', icon: '⚡', color: 'bg-green-100 border-green-300', visible: true, order: 8 },
    { id: 'ice_cream_flavors', value: 'ice_cream_flavors', label: 'Ice Cream Flavors', icon: '🎨', color: 'bg-indigo-100 border-indigo-300', visible: true, order: 9 }
  ];

  useEffect(() => {
    fetchItems();
    fetchCategories();
    fetchImageHealth();
  }, []);

  const fetchImageHealth = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items/image-health`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBrokenImages(data.broken || []);
        setBrokenImageIds(new Set((data.broken || []).map(b => b.id)));
      }
    } catch (error) {
      console.error('Error fetching image health:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items/categories`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Map backend format to component format
        const mapped = data.map(cat => ({
          ...cat,
          value: cat.id // Frontend uses 'value', backend uses 'id'
        }));
        setCategories(mapped);
        
        // Initialize expanded sections
        const initialExpanded = {};
        mapped.forEach(cat => {
          initialExpanded[cat.id] = true;
        });
        setExpandedSections(initialExpanded);
      } else {
        // Use defaults if backend fails
        setCategories(defaultCategories);
        const initialExpanded = {};
        defaultCategories.forEach(cat => {
          initialExpanded[cat.value] = true;
        });
        setExpandedSections(initialExpanded);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories(defaultCategories);
    }
  };

  const toggleCategoryVisibility = async (categoryId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items/categories/${categoryId}/toggle-visibility`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        // Update local state
        setCategories(prev => prev.map(cat => 
          cat.id === categoryId ? { ...cat, visible: result.visible } : cat
        ));
        toast({
          title: result.visible ? 'Category Visible' : 'Category Hidden',
          description: `${categories.find(c => c.id === categoryId)?.label} is now ${result.visible ? 'visible' : 'hidden'} on the public menu.`
        });
      }
    } catch (error) {
      console.error('Error toggling visibility:', error);
      toast({
        title: 'Error',
        description: 'Failed to update category visibility',
        variant: 'destructive'
      });
    }
  };

  // Open category pricing dialog
  const openCategoryPricingDialog = (category) => {
    setEditingCategoryPricing(category);
    setCategoryPricingForm({
      pricing_type: category.pricing_type || 'individual',
      shared_prices: category.shared_prices || []
    });
  };

  // Save category pricing
  const saveCategoryPricing = async () => {
    if (!editingCategoryPricing) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items/categories/${editingCategoryPricing.id}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pricing_type: categoryPricingForm.pricing_type,
          shared_prices: categoryPricingForm.pricing_type === 'shared' 
            ? categoryPricingForm.shared_prices.filter(p => p.name && p.price)
            : null
        })
      });
      
      if (response.ok) {
        const updated = await response.json();
        setCategories(prev => prev.map(cat => 
          cat.id === editingCategoryPricing.id ? { ...cat, ...updated } : cat
        ));
        toast({
          title: 'Pricing Updated',
          description: `Pricing configuration saved for ${editingCategoryPricing.label}`
        });
        setEditingCategoryPricing(null);
      }
    } catch (error) {
      console.error('Error saving category pricing:', error);
      toast({
        title: 'Error',
        description: 'Failed to save pricing configuration',
        variant: 'destructive'
      });
    }
  };

  // Add shared price option
  const addSharedPrice = () => {
    setCategoryPricingForm({
      ...categoryPricingForm,
      shared_prices: [...categoryPricingForm.shared_prices, { name: '', price: '' }]
    });
  };

  // Update shared price
  const updateSharedPrice = (index, field, value) => {
    const newPrices = [...categoryPricingForm.shared_prices];
    newPrices[index] = { ...newPrices[index], [field]: value };
    setCategoryPricingForm({ ...categoryPricingForm, shared_prices: newPrices });
  };

  // Remove shared price
  const removeSharedPrice = (index) => {
    const newPrices = categoryPricingForm.shared_prices.filter((_, i) => i !== index);
    setCategoryPricingForm({ ...categoryPricingForm, shared_prices: newPrices });
  };

  // Get pricing type label
  const getPricingTypeLabel = (type) => {
    switch (type) {
      case 'shared': return 'Shared Pricing';
      case 'individual': return 'Individual Pricing';
      case 'none': return 'No Pricing';
      default: return 'Individual Pricing';
    }
  };

  // Image drag & drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Maximum file size is 5MB. Please compress or resize the image.',
          variant: 'destructive'
        });
        return;
      }
      
      // Upload to server
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload
        });
        
        if (response.ok) {
          const result = await response.json();
          setFormData({ ...formData, image_url: result.url });
          toast({ title: 'Image uploaded', description: 'Image saved successfully' });
        } else {
          const error = await response.json();
          toast({ title: 'Upload failed', description: error.detail || 'Failed to upload image', variant: 'destructive' });
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast({ title: 'Upload failed', description: 'Network error', variant: 'destructive' });
      }
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: 'File too large',
          description: 'Maximum file size is 5MB. Please compress or resize the image.',
          variant: 'destructive'
        });
        return;
      }
      
      // Upload to server instead of using Base64
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload
        });
        
        if (response.ok) {
          const result = await response.json();
          setFormData({ ...formData, image_url: result.url });
          toast({
            title: 'Image uploaded',
            description: 'Image saved successfully'
          });
        } else {
          const error = await response.json();
          toast({
            title: 'Upload failed',
            description: error.detail || 'Failed to upload image',
            variant: 'destructive'
          });
        }
      } catch (error) {
        console.error('Upload error:', error);
        toast({
          title: 'Upload failed',
          description: 'Network error uploading image',
          variant: 'destructive'
        });
      }
    }
  };

  const fetchItems = async () => {
    try {
      const data = await getMenuItems();
      setItems(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load menu items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (categoryValue) => {
    setExpandedSections(prev => ({
      ...prev,
      [categoryValue]: !prev[categoryValue]
    }));
  };

  const getItemsByCategory = (categoryValue) => {
    return items.filter(item => item.category === categoryValue).sort((a, b) => a.order - b.order);
  };

  // Toggle item on/off Specials page
  const toggleOnSpecials = async (itemId, currentStatus) => {
    try {
      const token = localStorage.getItem('token');
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/menu-items/${itemId}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ on_specials: !currentStatus })
      });
      fetchItems();
      toast({
        title: !currentStatus ? 'Added to Specials' : 'Removed from Specials',
        description: !currentStatus 
          ? 'Go to Specials to set special price & description' 
          : 'Item removed from Specials page'
      });
    } catch (error) {
      console.error('Error updating item:', error);
      toast({
        title: 'Error',
        description: 'Failed to update item',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const hasSizes = item.sizes && item.sizes.length > 0;
    setUseSizes(hasSizes);
    setFormData({
      name: item.name,
      description: item.description || '',
      price: item.price || '',
      sizes: item.sizes || [],
      show_price: item.show_price !== false,
      show_image: item.show_image !== false,
      image_url: item.image_url || '',
      image_alt: item.image_alt || '',
      category: item.category,
      display_page: item.display_page || 'menu',
      featured: item.featured,
      famous: item.famous,
      active: item.active,
      order: item.order
    });
    setIsDialogOpen(true);
  };

  const handleAddToSection = (categoryValue) => {
    setEditingItem(null);
    setSelectedCategory(categoryValue);
    setUseSizes(false);
    const categoryItems = getItemsByCategory(categoryValue);
    const maxOrder = categoryItems.length > 0 ? Math.max(...categoryItems.map(i => i.order)) : 0;
    
    setFormData({
      name: '',
      description: '',
      price: '',
      sizes: [],
      show_price: true,
      show_image: true,
      image_url: '',
      image_alt: '',
      category: categoryValue,
      display_page: 'menu',
      featured: false,
      famous: false,
      active: true,
      order: maxOrder + 1
    });
    setIsDialogOpen(true);
  };

  // Size management functions
  const addSize = () => {
    setFormData({
      ...formData,
      sizes: [...formData.sizes, { name: '', price: '', description: '', image_url: '', image_alt: '' }]
    });
  };

  const updateSize = (index, field, value) => {
    const newSizes = [...formData.sizes];
    newSizes[index] = { ...newSizes[index], [field]: value };
    setFormData({ ...formData, sizes: newSizes });
  };

  const removeSize = (index) => {
    const newSizes = formData.sizes.filter((_, i) => i !== index);
    setFormData({ ...formData, sizes: newSizes });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        price: useSizes ? null : (formData.price ? parseFloat(formData.price) : null),
        sizes: useSizes ? formData.sizes.map(s => ({
          name: s.name,
          price: parseFloat(s.price) || 0,
          description: s.description || null,
          image_url: s.image_url || null,
          image_alt: s.image_alt || null  // Include alt text for SEO
        })).filter(s => s.name && s.price) : null
      };

      if (editingItem) {
        await updateMenuItem(editingItem.id, submitData);
        toast({
          title: 'Success',
          description: 'Menu item updated successfully'
        });
      } else {
        await createMenuItem(submitData);
        toast({
          title: 'Success',
          description: 'Menu item created successfully'
        });
      }
      
      setIsDialogOpen(false);
      fetchItems();
      fetchImageHealth();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save menu item',
        variant: 'destructive'
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await deleteMenuItem(id);
      toast({
        title: 'Success',
        description: 'Menu item deleted successfully'
      });
      fetchItems();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete menu item',
        variant: 'destructive'
      });
    }
  };

  const expandAll = () => {
    const allExpanded = {};
    categories.forEach(cat => {
      allExpanded[cat.value] = true;
    });
    setExpandedSections(allExpanded);
  };

  const collapseAll = () => {
    const allCollapsed = {};
    categories.forEach(cat => {
      allCollapsed[cat.value] = false;
    });
    setExpandedSections(allCollapsed);
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  return (
    <div className="max-w-full overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-3xl font-black">Menu Items</h2>
          <p className="text-gray-600 mt-1">Organize your menu by sections. Click on a section to expand/collapse.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={expandAll} className="text-sm">
            Expand All
          </Button>
          <Button variant="outline" onClick={collapseAll} className="text-sm">
            Collapse All
          </Button>
        </div>
      </div>

      {/* Instructions Box */}
      <div className="mb-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl">
        <div className="flex items-start gap-3">
          <span className="text-2xl">💡</span>
          <div className="text-sm">
            <p className="font-bold text-yellow-800 mb-1">How Specials Work:</p>
            <ul className="text-yellow-700 space-y-1">
              <li>• <strong>Toggle "⭐ On Specials"</strong> on any item to add it to the Specials page</li>
              <li>• Go to <strong>Specials</strong> in the sidebar to set special prices, descriptions & dates</li>
              <li>• Use <strong>👁 Visible/Hidden</strong> to control which categories show on the public Menu page</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Broken Image Alert Banner */}
      {brokenImages.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 border-2 border-red-300 rounded-xl" data-testid="broken-image-alert-banner">
          <div className="flex items-start gap-3">
            <span className="text-2xl">⚠️</span>
            <div className="text-sm flex-1">
              <p className="font-bold text-red-800 mb-1">
                {brokenImages.length} menu {brokenImages.length === 1 ? 'item has' : 'items have'} a broken image
              </p>
              <p className="text-red-700 mb-2">
                These items point to an image that can't be loaded. Edit each item below and re-upload its photo:
              </p>
              <ul className="space-y-1">
                {brokenImages.map((b) => (
                  <li key={b.id} className="flex items-center gap-2" data-testid={`broken-image-list-${b.id}`}>
                    <span className="font-semibold text-red-900">{b.name}</span>
                    <span className="text-xs text-red-500">({b.category})</span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="ml-auto h-7 gap-1 border-red-300 text-red-700 hover:bg-red-100"
                      onClick={() => {
                        const target = items.find(it => it.id === b.id);
                        if (target) {
                          handleEdit(target);
                        } else {
                          toast({ title: 'Item not directly editable', description: `"${b.name}" is inactive or in a legacy category. Toggle it active or fix its category to edit here.`, variant: 'destructive' });
                        }
                      }}
                    >
                      <Pencil className="w-3 h-3" /> Fix
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Category Sections */}
      <div className="space-y-4">
        {categories.map((category) => {
          const categoryItems = getItemsByCategory(category.value || category.id);
          const isExpanded = expandedSections[category.value || category.id];
          const isVisible = category.visible !== false; // Default to visible
          const pricingType = category.pricing_type || 'individual';
          
          return (
            <div 
              key={category.value || category.id} 
              className={`border-2 rounded-xl overflow-hidden ${category.color} ${!isVisible ? 'opacity-60' : ''}`}
            >
              {/* Section Header */}
              <div 
                className="grid grid-cols-[1fr_auto] items-center gap-2 p-4 cursor-pointer hover:bg-white/50 transition-colors"
                onClick={() => toggleSection(category.value || category.id)}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-2xl flex-shrink-0">{category.icon}</span>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-xl font-bold truncate">{category.label}</h3>
                      {!isVisible && (
                        <span className="text-xs bg-red-500 text-white px-2 py-0.5 rounded">Hidden</span>
                      )}
                      {/* Pricing Type Badge */}
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        pricingType === 'shared' 
                          ? 'bg-blue-100 text-blue-700' 
                          : pricingType === 'none' 
                            ? 'bg-gray-100 text-gray-600' 
                            : 'bg-purple-100 text-purple-700'
                      }`}>
                        {pricingType === 'shared' && category.shared_prices?.length > 0
                          ? `💰 ${category.shared_prices.map(p => `${p.name}: $${p.price}`).join(', ')}`
                          : getPricingTypeLabel(pricingType)
                        }
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">{categoryItems.length} item{categoryItems.length !== 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Pricing Settings Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      openCategoryPricingDialog(category);
                    }}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg border-2 border-blue-400 bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100"
                    title="Configure pricing for this category"
                  >
                    <DollarSign className="w-4 h-4" />
                    <span className="hidden sm:inline">Pricing</span>
                  </button>
                  {/* Visibility Toggle Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleCategoryVisibility(category.id || category.value);
                    }}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                      isVisible 
                        ? 'border-green-400 bg-green-50 text-green-700 hover:bg-green-100' 
                        : 'border-red-400 bg-red-50 text-red-700 hover:bg-red-100'
                    }`}
                    title={isVisible ? 'Visible on menu - Click to hide' : 'Hidden from menu - Click to show'}
                  >
                    {isVisible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    <span className="hidden sm:inline">{isVisible ? 'Visible' : 'Hidden'}</span>
                  </button>
                  {/* Add Item Button */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToSection(category.value || category.id);
                    }}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-pink-600 text-white text-sm font-medium hover:bg-pink-700"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Add</span>
                  </button>
                  {/* Expand/Collapse Arrow */}
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-500" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-500" />
                  )}
                </div>
              </div>

              {/* Section Content */}
              {isExpanded && (
                <div className="bg-white p-4 border-t">
                  {categoryItems.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p className="mb-2">No items in this section yet</p>
                      <Button
                        onClick={() => handleAddToSection(category.value || category.id)}
                        variant="outline"
                        className="gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        Add First Item
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {categoryItems.map((item, index) => (
                        <div 
                          key={item.id} 
                          className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          {/* Mobile Layout - Stack vertically */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                            {/* Top row: Number, Image, Name */}
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              {/* Drag Handle */}
                              <div className="text-gray-400 cursor-grab hidden sm:block">
                                <GripVertical className="w-5 h-5" />
                              </div>
                              
                              {/* Order Number */}
                              <span className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-pink-100 text-pink-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                                {index + 1}
                              </span>
                              
                              {/* Image */}
                              {item.image_url ? (
                                <div className="relative flex-shrink-0">
                                  <img
                                    src={getUploadImageUrl(item.image_url)}
                                    alt={item.name}
                                    className={`w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg bg-gray-100 ${item.show_image === false ? 'opacity-40' : ''}`}
                                    onError={(e) => {
                                      e.target.style.display = 'none';
                                      e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                    }}
                                  />
                                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-lg items-center justify-center text-gray-400 text-xs hidden">
                                    Error
                                  </div>
                                  {item.show_image === false && (
                                    <div className="absolute inset-0 flex items-center justify-center">
                                      <span className="bg-gray-800 text-white text-xs px-1 rounded">Hidden</span>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-xs flex-shrink-0">
                                  No Img
                                </div>
                              )}
                              
                              {/* Item Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-1 flex-wrap">
                                  <h4 className="font-bold text-base sm:text-lg truncate max-w-[150px] sm:max-w-none">{item.name}</h4>
                                  {!item.active && (
                                    <span className="bg-gray-200 text-gray-600 text-xs px-1 py-0.5 rounded">Off</span>
                                  )}
                                  {item.featured && (
                                    <span className="bg-teal-100 text-teal-700 text-xs px-1 py-0.5 rounded">⭐</span>
                                  )}
                                  {item.famous && (
                                    <span className="bg-pink-100 text-pink-700 text-xs px-1 py-0.5 rounded">🌟</span>
                                  )}
                                  {brokenImageIds.has(item.id) && (
                                    <span className="bg-red-100 text-red-700 border border-red-300 text-xs px-1.5 py-0.5 rounded font-bold flex items-center gap-1" title="This item's image URL is unreachable. Re-upload the photo." data-testid={`broken-image-badge-${item.id}`}>
                                      ⚠ Broken image
                                    </span>
                                  )}
                                </div>
                                {item.description && (
                                  <p className="text-xs sm:text-sm text-gray-600 truncate">{item.description}</p>
                                )}
                                {/* Price on mobile */}
                                <div className="sm:hidden mt-1">
                                  {item.sizes && item.sizes.length > 0 ? (
                                    <span className="text-xs text-pink-600 font-bold">
                                      {item.sizes.length} sizes
                                    </span>
                                  ) : item.price ? (
                                    <span className="text-sm font-bold text-pink-600">${item.price.toFixed(2)}</span>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                            
                            {/* Price / Sizes - Desktop only */}
                            <div className="text-right hidden xl:block min-w-[100px]">
                              {item.sizes && item.sizes.length > 0 ? (
                                <div className="text-sm">
                                  {item.sizes.slice(0, 2).map((size, i) => (
                                    <div key={i} className="flex justify-between gap-2">
                                      <span className="text-gray-600 text-xs">{size.name}:</span>
                                      <span className="font-bold text-pink-600">${size.price?.toFixed(2)}</span>
                                    </div>
                                  ))}
                                  {item.sizes.length > 2 && (
                                    <span className="text-xs text-gray-400">+{item.sizes.length - 2} more</span>
                                  )}
                                </div>
                              ) : item.price ? (
                                <span className={`font-bold text-lg ${item.show_price !== false ? 'text-pink-600' : 'text-gray-400'}`}>
                                  ${item.price.toFixed(2)}
                                  {item.show_price === false && (
                                    <span className="block text-xs text-gray-400">Hidden</span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-gray-400">No price</span>
                              )}
                            </div>
                            
                            {/* Actions row */}
                            <div className="flex items-center gap-2 flex-shrink-0 justify-end sm:justify-start">
                              {/* On Specials Toggle - Compact on mobile */}
                              <button
                                onClick={() => toggleOnSpecials(item.id, item.on_specials)}
                                className={`flex items-center gap-1 px-2 sm:px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${
                                  item.on_specials 
                                    ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400' 
                                    : 'bg-gray-100 text-gray-500 border-2 border-gray-300'
                                }`}
                                title={item.on_specials ? 'On Specials page - Click to remove' : 'Add to Specials page'}
                              >
                                ⭐ <span className="hidden sm:inline">{item.on_specials ? 'On Specials' : 'Add'}</span>
                              </button>
                              
                              {/* Edit & Delete */}
                              <Button
                                onClick={() => handleEdit(item)}
                                variant="outline"
                                size="sm"
                                className="gap-1 px-2 sm:px-3"
                              >
                                <Pencil className="w-4 h-4" />
                                <span className="hidden sm:inline">Edit</span>
                              </Button>
                              <Button
                                onClick={() => handleDelete(item.id)}
                                variant="destructive"
                                size="sm"
                                className="px-2 sm:px-3"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Uncategorized / Legacy items (category not in the known category list) */}
      {(() => {
        const knownCats = new Set(categories.map(c => c.value || c.id));
        const orphans = items.filter(it => !knownCats.has(it.category));
        if (orphans.length === 0) return null;
        return (
          <div className="mt-4 border-2 border-dashed border-gray-400 rounded-xl overflow-hidden bg-gray-50" data-testid="uncategorized-section">
            <div className="p-4 bg-gray-100">
              <h3 className="text-xl font-bold flex items-center gap-2">🗂️ Uncategorized / Legacy Items</h3>
              <p className="text-sm text-gray-600">{orphans.length} item{orphans.length !== 1 ? 's' : ''} in old/unknown categories. Fix the category (Edit) or delete them.</p>
            </div>
            <div className="bg-white p-4 border-t space-y-3">
              {orphans.map((item) => (
                <div key={item.id} className="p-3 bg-gray-50 rounded-lg flex items-center gap-3" data-testid={`orphan-item-${item.id}`}>
                  {item.image_url ? (
                    <img src={getUploadImageUrl(item.image_url)} alt={item.name} className="w-14 h-14 object-cover rounded-lg bg-gray-100"
                      onError={(e) => { e.target.style.visibility = 'hidden'; }} />
                  ) : (
                    <div className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-xs">No Img</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h4 className="font-bold truncate">{item.name}</h4>
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-0.5 rounded">category: {item.category || '(none)'}</span>
                      {!item.active && <span className="text-xs bg-gray-300 text-gray-600 px-1.5 py-0.5 rounded">inactive</span>}
                      {brokenImageIds.has(item.id) && (
                        <span className="bg-red-100 text-red-700 border border-red-300 text-xs px-1.5 py-0.5 rounded font-bold">⚠ Broken image</span>
                      )}
                    </div>
                  </div>
                  <Button onClick={() => handleEdit(item)} variant="outline" size="sm" className="gap-1"><Pencil className="w-4 h-4" /> Edit</Button>
                  <Button onClick={() => handleDelete(item.id)} variant="destructive" size="sm"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Summary Footer */}
      <div className="mt-6 p-4 bg-gray-100 rounded-xl">
        <div className="flex justify-between items-center">
          <div>
            <span className="font-bold">Total Items: </span>
            <span className="text-pink-600 font-bold">{items.length}</span>
          </div>
          <div className="flex gap-4 text-sm text-gray-600">
            <span>Active: {items.filter(i => i.active).length}</span>
            <span>On Specials: {items.filter(i => i.on_specials).length}</span>
            <span>Featured: {items.filter(i => i.featured).length}</span>
            <span>Famous: {items.filter(i => i.famous).length}</span>
          </div>
        </div>
      </div>

      {/* Add/Edit Item Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-black">
              {editingItem ? 'Edit Menu Item' : `Add New Item to ${categories.find(c => c.value === formData.category)?.label || 'Menu'}`}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-bold mb-2">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  placeholder="Item name"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                >
                  {categories.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {cat.icon} {cat.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Display On Page *</label>
                <select
                  value={formData.display_page || 'menu'}
                  onChange={(e) => setFormData({ ...formData, display_page: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                >
                  <option value="menu">📋 Menu Page Only</option>
                  <option value="specials">⭐ Specials Page Only</option>
                  <option value="both">📋⭐ Both Pages</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose where this item should appear on the website
                </p>
              </div>

              {/* Pricing Toggle */}
              <div className="col-span-2 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-4 mb-3">
                  <span className="font-bold">Pricing Type:</span>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="pricingType"
                      checked={!useSizes}
                      onChange={() => setUseSizes(false)}
                      className="accent-pink-500"
                    />
                    <span>Single Price</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="pricingType"
                      checked={useSizes}
                      onChange={() => setUseSizes(true)}
                      className="accent-pink-500"
                    />
                    <span>Multiple Sizes</span>
                  </label>
                </div>

                {!useSizes ? (
                  <div>
                    <label className="block text-sm font-bold mb-2">Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                      placeholder="5.99"
                    />
                  </div>
                ) : (
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-sm font-bold">Size Options</label>
                      <Button
                        type="button"
                        onClick={addSize}
                        size="sm"
                        className="bg-pink-500 hover:bg-pink-600 gap-1"
                      >
                        <Plus className="w-3 h-3" /> Add Size
                      </Button>
                    </div>
                    
                    {formData.sizes.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">
                        Click "Add Size" to add size options (e.g., Small, Medium, Large)
                      </p>
                    ) : (
                      <div className="space-y-3">
                        {formData.sizes.map((size, index) => (
                          <div key={index} className="p-3 bg-white rounded-lg border space-y-2">
                            {/* Row 1: Name, Price, Delete */}
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={size.name}
                                onChange={(e) => updateSize(index, 'name', e.target.value)}
                                placeholder="Size name (e.g., Baby)"
                                className="flex-1 px-3 py-2 rounded border focus:border-pink-500 focus:outline-none"
                              />
                              <div className="flex items-center gap-1">
                                <span className="text-gray-400">$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={size.price}
                                  onChange={(e) => updateSize(index, 'price', e.target.value)}
                                  placeholder="0.00"
                                  className="w-20 px-2 py-2 rounded border focus:border-pink-500 focus:outline-none"
                                />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeSize(index)}
                                className="p-2 text-red-500 hover:bg-red-50 rounded"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            {/* Row 2: Description */}
                            <input
                              type="text"
                              value={size.description || ''}
                              onChange={(e) => updateSize(index, 'description', e.target.value)}
                              placeholder="Note/Description (optional)"
                              className="w-full px-3 py-2 rounded border focus:border-pink-500 focus:outline-none text-sm"
                            />
                            {/* Row 3: Image Upload */}
                            <div className="flex items-center gap-2 flex-wrap">
                              {size.image_url ? (
                                <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded px-3 py-2">
                                  <span className="text-green-600 text-lg">✓</span>
                                  <div className="flex flex-col">
                                    <span className="text-xs text-green-700 font-medium">Image uploaded</span>
                                    <span className="text-xs text-gray-500 truncate max-w-[150px]">
                                      {size.image_url.split('/').pop()}
                                    </span>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => updateSize(index, 'image_url', '')}
                                    className="ml-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1"
                                    title="Remove image"
                                  >
                                    <X className="w-3 h-3" />
                                  </button>
                                </div>
                              ) : (
                                <div className="flex gap-2">
                                  <label className="cursor-pointer flex items-center gap-2 px-3 py-2 bg-pink-50 hover:bg-pink-100 rounded border border-pink-200 text-sm text-pink-700 font-medium">
                                    <Upload className="w-4 h-4" />
                                    <span>Upload</span>
                                    <input
                                      type="file"
                                      accept="image/*"
                                      onChange={async (e) => {
                                        const file = e.target.files?.[0];
                                        if (file) {
                                          if (file.size > 5 * 1024 * 1024) {
                                            toast({ title: 'File too large', description: 'Max 5MB', variant: 'destructive' });
                                            return;
                                          }
                                          const formDataUpload = new FormData();
                                          formDataUpload.append('file', file);
                                          try {
                                            const token = localStorage.getItem('token');
                                            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/image`, {
                                              method: 'POST',
                                              headers: { 'Authorization': `Bearer ${token}` },
                                              body: formDataUpload
                                            });
                                            if (response.ok) {
                                              const result = await response.json();
                                              updateSize(index, 'image_url', result.url);
                                              toast({ title: '✓ Image uploaded!', description: file.name });
                                            } else {
                                              const err = await response.json().catch(() => ({}));
                                              toast({ title: 'Upload failed', description: err.detail || 'Please try again', variant: 'destructive' });
                                            }
                                          } catch (err) {
                                            console.error('Upload error:', err);
                                            toast({ title: 'Upload failed', description: 'Network error', variant: 'destructive' });
                                          }
                                        }
                                      }}
                                      className="hidden"
                                    />
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setImagePickerTarget(index);
                                      setShowImagePicker(true);
                                    }}
                                    className="flex items-center gap-2 px-3 py-2 bg-purple-50 hover:bg-purple-100 rounded border border-purple-200 text-sm text-purple-700 font-medium"
                                  >
                                    <Image className="w-4 h-4" />
                                    <span>Gallery</span>
                                  </button>
                                </div>
                              )}
                              <input
                                type="text"
                                value={size.image_url || ''}
                                onChange={(e) => updateSize(index, 'image_url', e.target.value)}
                                placeholder="Or paste image URL"
                                className="flex-1 px-3 py-2 rounded border focus:border-pink-500 focus:outline-none text-sm"
                              />
                            </div>
                            {/* Alt Text for Size Image */}
                            <input
                              type="text"
                              value={size.image_alt || ''}
                              onChange={(e) => updateSize(index, 'image_alt', e.target.value)}
                              placeholder="Alt text for SEO (e.g., Baby scoop vanilla ice cream)"
                              className="w-full px-3 py-2 rounded border focus:border-pink-500 focus:outline-none text-xs mt-1"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-bold mb-2">Image (Upload or paste URL)</label>
                <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                  <strong>📐 Recommended:</strong> 400×400px (1:1 square) for menu items. Max 1MB. JPG, PNG, or WebP.
                </div>
                
                {/* Image Upload Zone */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors mb-2 ${
                    isDragging ? 'border-pink-500 bg-pink-50' : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  {formData.image_url ? (
                    <div className="relative inline-block">
                      <img 
                        src={getUploadImageUrl(formData.image_url)} 
                        alt="Preview" 
                        className={`max-h-40 mx-auto rounded-lg ${!formData.show_image ? 'opacity-50' : ''}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image_url: '' })}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-1 transform translate-x-1/2 -translate-y-1/2 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="py-2">
                      <Upload className="w-8 h-8 mx-auto text-gray-400 mb-2 hidden sm:block" />
                      <p className="text-gray-500 mb-3 hidden sm:block">Drag & drop an image here, or</p>
                      
                      {/* Mobile-friendly upload buttons */}
                      <div className="flex flex-col sm:flex-row gap-2 justify-center items-center">
                        <label className="cursor-pointer w-full sm:w-auto">
                          <span className="inline-flex items-center justify-center gap-2 bg-pink-500 hover:bg-pink-600 text-white px-6 py-3 rounded-lg text-sm font-bold w-full sm:w-auto">
                            <Upload className="w-4 h-4" />
                            Upload Image
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                        </label>
                        
                        {/* Choose from Gallery button */}
                        <button
                          type="button"
                          onClick={() => {
                            setImagePickerTarget('main');
                            setShowImagePicker(true);
                          }}
                          className="inline-flex items-center justify-center gap-2 bg-purple-500 hover:bg-purple-600 text-white px-6 py-3 rounded-lg text-sm font-bold w-full sm:w-auto"
                        >
                          <Image className="w-4 h-4" />
                          Choose from Gallery
                        </button>
                        
                        {/* Camera capture for mobile */}
                        <label className="cursor-pointer w-full sm:w-auto sm:hidden">
                          <span className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg text-sm font-bold w-full">
                            📷 Take Photo
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={handleFileSelect}
                            className="hidden"
                          />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* URL Input */}
                <input
                  type="text"
                  value={formData.image_url.startsWith('data:') ? '' : formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  placeholder="Or paste image URL here..."
                />
                
                {/* Alt Text Input for SEO */}
                <div className="mt-3">
                  <label className="block text-xs font-bold mb-1 text-gray-600">
                    Image Alt Text (for SEO)
                  </label>
                  <input
                    type="text"
                    value={formData.image_alt || ''}
                    onChange={(e) => setFormData({ ...formData, image_alt: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none text-sm"
                    placeholder="e.g., Chocolate ice cream scoop in white cup"
                  />
                </div>
                
                <div className="flex items-center gap-4 mt-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.show_image}
                      onChange={(e) => setFormData({ ...formData, show_image: e.target.checked })}
                      className="w-5 h-5 accent-pink-500"
                    />
                    <span className="font-bold text-sm">Show Image on Menu</span>
                  </label>
                  {!formData.show_image && (
                    <span className="text-xs text-gray-500 italic">(Image hidden from public menu)</span>
                  )}
                </div>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-bold mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="2"
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                  placeholder="Brief description of the item"
                />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Display Order</label>
                <input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                  className="w-full px-4 py-2 rounded-lg border-2 focus:border-pink-500 focus:outline-none"
                />
              </div>

              {!useSizes && (
                <div className="flex items-center">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.show_price}
                      onChange={(e) => setFormData({ ...formData, show_price: e.target.checked })}
                      className="w-5 h-5 accent-pink-500"
                    />
                    <span className="font-bold">Show Price on Menu</span>
                  </label>
                </div>
              )}
            </div>

            {/* Status Flags */}
            <div className="flex gap-6 p-4 bg-gray-50 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  className="w-5 h-5 accent-green-500"
                />
                <span className="font-bold">Active</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-5 h-5 accent-teal-500"
                />
                <span className="font-bold">Featured</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.famous}
                  onChange={(e) => setFormData({ ...formData, famous: e.target.checked })}
                  className="w-5 h-5 accent-pink-500"
                />
                <span className="font-bold">Famous</span>
              </label>
            </div>

            <div className="flex gap-4 pt-4">
              <Button type="submit" className="flex-1 bg-pink-600 hover:bg-pink-700">
                {editingItem ? 'Update Item' : 'Add Item'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Category Pricing Dialog */}
      <Dialog open={!!editingCategoryPricing} onOpenChange={(open) => !open && setEditingCategoryPricing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-xl font-black flex items-center gap-2">
              <DollarSign className="w-6 h-6 text-blue-600" />
              Pricing Settings: {editingCategoryPricing?.label}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 pt-4">
            {/* Pricing Type Selection */}
            <div className="space-y-3">
              <label className="block font-bold text-sm">How should prices be displayed?</label>
              
              <div className="space-y-2">
                <label className="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="pricingType"
                    checked={categoryPricingForm.pricing_type === 'shared'}
                    onChange={() => setCategoryPricingForm({ ...categoryPricingForm, pricing_type: 'shared' })}
                    className="mt-1 accent-blue-500"
                  />
                  <div>
                    <span className="font-bold text-blue-700">Shared Pricing</span>
                    <p className="text-sm text-gray-600">One set of prices shown once for the entire section (e.g., Regular $7, Large $9 for all Stackers)</p>
                  </div>
                </label>
                
                <label className="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="pricingType"
                    checked={categoryPricingForm.pricing_type === 'individual'}
                    onChange={() => setCategoryPricingForm({ ...categoryPricingForm, pricing_type: 'individual' })}
                    className="mt-1 accent-purple-500"
                  />
                  <div>
                    <span className="font-bold text-purple-700">Individual Pricing</span>
                    <p className="text-sm text-gray-600">Each item has its own price (set in item edit form)</p>
                  </div>
                </label>
                
                <label className="flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer hover:bg-gray-50 transition-colors">
                  <input
                    type="radio"
                    name="pricingType"
                    checked={categoryPricingForm.pricing_type === 'none'}
                    onChange={() => setCategoryPricingForm({ ...categoryPricingForm, pricing_type: 'none' })}
                    className="mt-1 accent-gray-500"
                  />
                  <div>
                    <span className="font-bold text-gray-700">No Pricing</span>
                    <p className="text-sm text-gray-600">This section doesn't show any prices (e.g., flavor lists)</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Shared Prices Editor (only show when pricing_type is 'shared') */}
            {categoryPricingForm.pricing_type === 'shared' && (
              <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex justify-between items-center">
                  <label className="font-bold text-sm text-blue-800">Section Prices</label>
                  <Button
                    type="button"
                    onClick={addSharedPrice}
                    size="sm"
                    className="bg-blue-500 hover:bg-blue-600 gap-1"
                  >
                    <Plus className="w-3 h-3" /> Add Price
                  </Button>
                </div>
                
                {categoryPricingForm.shared_prices.length === 0 ? (
                  <p className="text-blue-600 text-sm text-center py-4">
                    Click "Add Price" to add size/price options (e.g., Regular $7.00, Large $9.00)
                  </p>
                ) : (
                  <div className="space-y-2">
                    {categoryPricingForm.shared_prices.map((price, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={price.name}
                          onChange={(e) => updateSharedPrice(index, 'name', e.target.value)}
                          placeholder="Size name (e.g., Regular)"
                          className="flex-1 px-3 py-2 rounded border focus:border-blue-500 focus:outline-none"
                        />
                        <div className="flex items-center">
                          <span className="text-gray-500 mr-1">$</span>
                          <input
                            type="number"
                            step="0.01"
                            value={price.price}
                            onChange={(e) => updateSharedPrice(index, 'price', parseFloat(e.target.value) || '')}
                            placeholder="0.00"
                            className="w-24 px-3 py-2 rounded border focus:border-blue-500 focus:outline-none"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSharedPrice(index)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={saveCategoryPricing}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Save Pricing Settings
              </Button>
              <Button
                variant="outline"
                onClick={() => setEditingCategoryPricing(null)}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Image Picker Modal */}
      <ImagePickerModal
        open={showImagePicker}
        onClose={() => {
          setShowImagePicker(false);
          setImagePickerTarget(null);
        }}
        onSelect={(url) => {
          if (imagePickerTarget === 'main') {
            setFormData({ ...formData, image_url: url });
          } else if (typeof imagePickerTarget === 'number') {
            // Size image
            updateSize(imagePickerTarget, 'image_url', url);
          }
          setShowImagePicker(false);
          setImagePickerTarget(null);
        }}
        currentImage={
          imagePickerTarget === 'main' 
            ? formData.image_url 
            : typeof imagePickerTarget === 'number' 
              ? formData.sizes[imagePickerTarget]?.image_url 
              : ''
        }
      />
    </div>
  );
};

export default MenuItems;
